/**
 * Attribution Service
 * 
 * Tracks signup attribution data including UTM parameters, referrer, campaign data, etc.
 * Stores attribution data in localStorage and sends to backend on signup
 */

export interface AttributionData {
  // UTM Parameters
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  
  // Referrer Data
  referrer?: string;
  referrer_domain?: string;
  
  // Campaign Data
  campaign_id?: string;
  campaign_name?: string;
  affiliate_id?: string;
  promo_code?: string;
  
  // Technical Data
  user_agent?: string;
  screen_resolution?: string;
  language?: string;
  timezone?: string;
  
  // Tracking Data
  landing_page?: string;
  session_id?: string;
  first_visit?: string;
  last_visit?: string;
  visit_count?: number;
  
  // Social/Organic
  social_network?: string;
  search_engine?: string;
  search_query?: string;
  
  // Custom tracking
  custom_data?: Record<string, any>;
}

class AttributionService {
  private readonly ATTRIBUTION_KEY = 'signup_attribution';
  private readonly SESSION_KEY = 'attribution_session';
  private readonly VISIT_COUNT_KEY = 'visit_count';
  
  /**
   * Capture attribution data from current page/session
   */
  captureAttribution(): AttributionData {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer;
    
    // Extract UTM parameters
    const utmData = {
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined,
      utm_term: urlParams.get('utm_term') || undefined,
      utm_content: urlParams.get('utm_content') || undefined,
    };
    
    // Extract other campaign parameters
    const campaignData = {
      campaign_id: urlParams.get('campaign_id') || urlParams.get('cid') || undefined,
      campaign_name: urlParams.get('campaign_name') || urlParams.get('cn') || undefined,
      affiliate_id: urlParams.get('affiliate_id') || urlParams.get('aff') || undefined,
      promo_code: urlParams.get('promo_code') || urlParams.get('promo') || undefined,
    };
    
    // Analyze referrer
    const referrerData = this.analyzeReferrer(referrer);
    
    // Get technical data
    const technicalData = {
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    
    // Get session data
    const sessionData = this.getSessionData();
    
    const attribution: AttributionData = {
      ...utmData,
      ...campaignData,
      ...referrerData,
      ...technicalData,
      ...sessionData,
      landing_page: window.location.href,
    };
    
    // Store attribution data
    this.storeAttribution(attribution);
    
    return attribution;
  }
  
  /**
   * Analyze referrer to extract useful information
   */
  private analyzeReferrer(referrer: string): Partial<AttributionData> {
    if (!referrer) return {};
    
    const referrerData: Partial<AttributionData> = {
      referrer: referrer,
    };
    
    try {
      const url = new URL(referrer);
      referrerData.referrer_domain = url.hostname;
      
      // Detect social networks
      const socialNetworks = {
        'facebook.com': 'Facebook',
        'twitter.com': 'Twitter',
        'x.com': 'X (Twitter)',
        'linkedin.com': 'LinkedIn',
        'instagram.com': 'Instagram',
        'youtube.com': 'YouTube',
        'tiktok.com': 'TikTok',
        'reddit.com': 'Reddit',
        'pinterest.com': 'Pinterest',
        'snapchat.com': 'Snapchat',
        'whatsapp.com': 'WhatsApp',
        'telegram.org': 'Telegram',
      };
      
      for (const [domain, network] of Object.entries(socialNetworks)) {
        if (url.hostname.includes(domain)) {
          referrerData.social_network = network;
          break;
        }
      }
      
      // Detect search engines
      const searchEngines = {
        'google.com': 'Google',
        'bing.com': 'Bing',
        'yahoo.com': 'Yahoo',
        'duckduckgo.com': 'DuckDuckGo',
        'baidu.com': 'Baidu',
        'yandex.com': 'Yandex',
      };
      
      for (const [domain, engine] of Object.entries(searchEngines)) {
        if (url.hostname.includes(domain)) {
          referrerData.search_engine = engine;
          // Try to extract search query
          const query = url.searchParams.get('q') || url.searchParams.get('query');
          if (query) {
            referrerData.search_query = query;
          }
          break;
        }
      }
    } catch (error) {
      console.warn('Failed to parse referrer URL:', error);
    }
    
    return referrerData;
  }
  
  /**
   * Get session tracking data
   */
  private getSessionData(): Partial<AttributionData> {
    const now = new Date().toISOString();
    
    // Generate or get session ID
    let sessionId = sessionStorage.getItem(this.SESSION_KEY);
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem(this.SESSION_KEY, sessionId);
    }
    
    // Track visit count
    const visitCount = parseInt(localStorage.getItem(this.VISIT_COUNT_KEY) || '0') + 1;
    localStorage.setItem(this.VISIT_COUNT_KEY, visitCount.toString());
    
    // Track first visit
    const firstVisit = localStorage.getItem('first_visit') || now;
    localStorage.setItem('first_visit', firstVisit);
    
    return {
      session_id: sessionId,
      first_visit: firstVisit,
      last_visit: now,
      visit_count: visitCount,
    };
  }
  
  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return 'sess_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }
  
  /**
   * Store attribution data in localStorage
   */
  private storeAttribution(attribution: AttributionData): void {
    try {
      localStorage.setItem(this.ATTRIBUTION_KEY, JSON.stringify(attribution));
    } catch (error) {
      console.warn('Failed to store attribution data:', error);
    }
  }
  
  /**
   * Get stored attribution data
   */
  getStoredAttribution(): AttributionData | null {
    try {
      const stored = localStorage.getItem(this.ATTRIBUTION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to retrieve attribution data:', error);
      return null;
    }
  }
  
  /**
   * Clear stored attribution data (called after successful signup)
   */
  clearAttribution(): void {
    localStorage.removeItem(this.ATTRIBUTION_KEY);
  }
  
  /**
   * Update attribution with custom data
   */
  updateAttribution(customData: Record<string, any>): void {
    const existing = this.getStoredAttribution();
    if (existing) {
      existing.custom_data = { ...existing.custom_data, ...customData };
      this.storeAttribution(existing);
    }
  }
  
  /**
   * Track conversion event
   */
  trackConversion(event: string, data?: Record<string, any>): void {
    const attribution = this.getStoredAttribution();
    if (attribution) {
      // Send conversion event to analytics
      this.sendConversionEvent(event, { ...attribution, ...data });
    }
  }
  
  /**
   * Send conversion event to backend
   */
  private async sendConversionEvent(event: string, data: any): Promise<void> {
    try {
      await fetch('/api/v1/analytics/conversion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event,
          data,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.warn('Failed to send conversion event:', error);
    }
  }
}

export const attributionService = new AttributionService();
export default attributionService; 